import { Controller } from "@hotwired/stimulus"
import { generateUniqueId } from "../utils/generate_unique_id"
import Sortable from "sortablejs"

export default class extends Controller {
  static bodyModalClass = "faq-preview-modal-open"
  static targets = ["container", "template", "row", "previewModal", "previewDialog", "previewContent", "previewContentWrapper", "previewEmptyState", "teaserInput", "previewTeaser"]

  connect() {
    this.syncPositions()
    this.sortable = Sortable.create(this.containerTarget, {
      handle: ".sortable-handle",
      animation: 150,
      ghostClass: "opacity-50",
      onEnd: () => this.syncPositions(),
    })
  }

  disconnect() {
    this.unlockBodyScroll()
    this.sortable?.destroy()
  }

  add(event) {
    event.preventDefault()
    this.addRowFromTemplate()
  }

  addRowFromTemplate() {
    const domId = generateUniqueId("faq-entry")
    const formIndex = Date.now()
    const template = this.templateTarget.innerHTML
      .replaceAll("NEW_DOM_ID", domId)
      .replaceAll("NEW_RECORD", formIndex)
    this.containerTarget.insertAdjacentHTML("beforeend", template)
    this.syncPositions()
  }

  syncPositions() {
    this.activeRowElements().forEach((row, index) => {
      const positionInput = row.querySelector("input[name$='[position]']")
      if (!positionInput) return

      positionInput.value = index + 1
    })
  }

  openPreview(event) {
    event.preventDefault()

    this.renderPreviewTeaser()
    this.renderPreviewRows()
    this.previewModalTarget.classList.remove("hidden")
    this.lockBodyScroll()
  }

  closePreview(event) {
    if (event) event.preventDefault()

    this.previewModalTarget.classList.add("hidden")
    this.unlockBodyScroll()
  }

  backdropClose(event) {
    if (event.target === this.previewModalTarget) {
      this.closePreview()
    }
  }

  renderPreviewRows() {
    this.previewContentTarget.replaceChildren()

    const activeRows = this.activeRows()
    if (activeRows.length === 0) {
      if (this.hasPreviewContentWrapperTarget) {
        this.previewContentWrapperTarget.classList.add("hidden")
        this.previewEmptyStateTarget.classList.remove("hidden")
        this.previewEmptyStateTarget.classList.add("flex")
        this.setPreviewDialogCompact(true)
      } else {
        this.previewEmptyStateTarget.classList.remove("hidden")
      }
      return
    }

    if (this.hasPreviewContentWrapperTarget) {
      this.previewContentWrapperTarget.classList.remove("hidden")
      this.previewEmptyStateTarget.classList.remove("flex")
      this.setPreviewDialogCompact(false)
    }
    this.previewEmptyStateTarget.classList.add("hidden")
    const accordionGroupName = `${this.previewModalTarget.id}-accordion-group`

    activeRows.forEach(({ question, answer }, index) => {
      const previewId = `faq-preview-entry-${index + 1}`
      const details = document.createElement("details")
      details.id = previewId
      details.name = accordionGroupName
      details.className = "group faq-accordion py-2"

      const summary = document.createElement("summary")
      summary.id = `${previewId}-question`
      summary.className = "list-none cursor-pointer flex items-center gap-3 text-black text-[18px] font-bold font-['Oswald']"

      const caret = document.createElement("span")
      caret.setAttribute("aria-hidden", "true")
      caret.className = "text-[10px] transition-transform group-open:rotate-90"
      caret.textContent = "\u25B6"

      const questionText = document.createElement("span")
      questionText.textContent = question

      summary.appendChild(caret)
      summary.appendChild(questionText)

      const answerDiv = document.createElement("div")
      answerDiv.id = `${previewId}-answer`
      answerDiv.className = "pt-2 pb-[10px] pl-5 pr-2 text-sm text-gray-700 leading-relaxed whitespace-pre-line"
      answerDiv.textContent = answer

      details.appendChild(summary)
      details.appendChild(answerDiv)
      this.previewContentTarget.appendChild(details)
    })
  }

  renderPreviewTeaser() {
    if (!this.hasPreviewTeaserTarget) return

    const teaser = this.hasTeaserInputTarget ? this.teaserInputTarget.value.trim() : ""
    this.previewTeaserTarget.textContent = teaser
    this.previewTeaserTarget.classList.toggle("hidden", teaser.length === 0)
  }

  lockBodyScroll() {
    document.body.classList.add(this.constructor.bodyModalClass)
  }

  unlockBodyScroll() {
    document.body.classList.remove(this.constructor.bodyModalClass)
  }

  setPreviewDialogCompact(compact) {
    if (!this.hasPreviewDialogTarget) return

    this.previewDialogTarget.classList.add("h-auto")
    this.previewDialogTarget.classList.toggle("max-h-[60vh]", !compact)
    this.previewDialogTarget.classList.toggle("max-h-[320px]", compact)
  }

  activeRows() {
    const rows = this.activeRowElements()
    const results = []

    rows.forEach((row) => {
      const destroyInput = row.querySelector("input[name*='[_destroy]']")
      if (destroyInput && destroyInput.value === "1") return

      const questionInput = row.querySelector("input[name$='[question]']")
      const answerInput = row.querySelector("textarea[name$='[answer]']")

      if (!questionInput || !answerInput) return

      const question = questionInput.value.trim()
      const answer = answerInput.value.trim()
      if (!question || !answer) return

      results.push({ question, answer })
    })

    return results
  }

  activeRowElements() {
    return this.rowTargets.filter((row) => {
      const destroyInput = row.querySelector("input[name*='[_destroy]']")
      return !destroyInput || destroyInput.value !== "1"
    })
  }
}
